import { isQualifiedEvidence } from '@/entities/attempt'
import type { LearningLayer } from '@/types'
import type { CompleteRemediationCertificationInput, RemediationCertificationResult } from './types'

function thresholdForLayer(layer: LearningLayer): number {
  return layer === 'A' ? 90 : layer === 'B' ? 80 : 70
}

export function completeRemediationCertification(
  input: CompleteRemediationCertificationInput,
): RemediationCertificationResult {
  const attempts = input.attempts
  if (attempts.length !== 5) throw new Error('Independent certification requires exactly five attempts.')
  if (input.authorizedQuestions.length !== 5
    || new Set(input.authorizedQuestions.map((question) => question.questionId)).size !== 5
    || new Set(input.authorizedQuestions.map((question) => question.contentFingerprint)).size !== 5) {
    throw new Error('Independent certification requires an authorized five-question manifest.')
  }
  const authorized = new Map(input.authorizedQuestions.map((question) => [question.questionId, question.contentFingerprint]))
  if (new Set(attempts.map((attempt) => attempt.exerciseId)).size !== 5
    || new Set(attempts.map((attempt) => attempt.contentFingerprint)).size !== 5
    || attempts.some((attempt) => (
      !attempt.contentFingerprint
      || authorized.get(attempt.exerciseId) !== attempt.contentFingerprint
    ))) {
    throw new Error('Independent certification questions must have unique identities and fingerprints.')
  }
  if (attempts.some((attempt) => (
    attempt.studentId !== input.wrongQuestion.studentId
    || attempt.stageId !== input.wrongQuestion.learningStageId
    || attempt.unitId !== input.wrongQuestion.unitId
    || attempt.knowledgePointId !== input.wrongQuestion.knowledgePointId
    || attempt.evidenceKind !== 'certification'
    || attempt.assessmentSessionId !== input.certificationRoundId
    || attempt.layer !== input.layer
    || attempt.hintCount !== 0
    || attempt.retryCount !== 0
    || !isQualifiedEvidence(attempt)
  ))) throw new Error('Certification attempts do not satisfy the independent evidence contract.')

  const threshold = thresholdForLayer(input.layer)
  const score = Math.round((attempts.filter((attempt) => attempt.correct).length / 5) * 100)
  const passed = score >= threshold
  const completedAt = input.completedAt ?? new Date().toISOString()
  return {
    passed,
    score,
    threshold,
    attempts,
    wrongQuestionPatch: {
      masteryState: passed ? 'mastered' : 'awaiting-certification',
      workflowState: 'idle',
      status: passed ? 'mastered' : 'awaiting-certification',
      certificationThreshold: threshold,
      certificationScore: score,
      certifiedAt: passed ? completedAt : undefined,
      variantAttempts: [
        ...input.wrongQuestion.variantAttempts,
        ...attempts.map((attempt) => ({
          attemptId: attempt.id,
          correct: attempt.correct,
          purpose: 'certification' as const,
          score: attempt.score,
          completedAt: attempt.completedAt,
        })),
      ],
    },
  }
}
